/**
 * Shared personalized-pricing preview for the family CART — one
 * submit-mode server preview per (campus, date, mealtime) group of the
 * cart's lines, combined into per-line lookups, per-sitting allowance
 * state, and cart-wide totals. The family surfaces aggregate every
 * campus's lines (a parent thinks in kids, not campuses), so the hook
 * splits the cart back into per-campus plans — the preview API prices
 * a single campus sitting per request.
 *
 * Used by the checkout page (line splits + footer totals) AND the
 * cart-total surfaces (day-view footer, week cart panel, mobile cart
 * bar) so every "what will I pay" number comes from the same
 * server-computed split that submit will charge. Query keys match
 * between all callers, so TanStack serves them from one cache.
 */

import { keepPreviousData, useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  type FamilyOrderPricePreviewLineWire,
  familyOrderPricePreviewQueryKey,
  type FamilyOrderPricePreviewStudentWire,
  fetchFamilyOrderPricePreview,
} from '@/api/family-orders'
import { type Mealtime, serviceDateIso } from '@/api/school-orders'
import { buildCheckoutPlan, type FamilyCartLine, groupKey } from '@/lib/family-orders/cart'
import {
  buildPreviewLookup,
  combinePreviewTotals,
  previewLineKey,
  type PreviewTotals,
  scopedPreviewEntries,
  sittingStudentKey,
} from '@/lib/family-orders/price-preview'
import { useAllCartLines } from '@/stores/family-cart'

export interface CartPricePreview {
  /**
   * Per-line preview keyed by `previewLineKey(groupKey(date, mealtime),
   * studentUserId, menuItemId)`. Sparse while groups are loading.
   */
  byKey: Map<string, FamilyOrderPricePreviewLineWire>
  /**
   * Cart-wide gross / school-covered / net. Null until EVERY group's
   * preview has loaded (callers fall back to the gross subtotal) or
   * when the cart is empty.
   */
  totals: PreviewTotals | null
  /**
   * Per-sitting allowance state keyed by
   * `sittingStudentKey(groupKey(date, mealtime), studentUserId)`.
   * Reflects prior submitted orders PLUS the cart's own lines (submit
   * mode) — `remainingSubsidizedUnits === 0` means the next add for
   * that sitting prices at the family rate.
   */
  studentsBySitting: Map<string, FamilyOrderPricePreviewStudentWire>
}

export function useCartPricePreview(): CartPricePreview {
  // Every campus's lines — split back into per-campus checkout plans so
  // each preview request carries the sitting's own serviceLocationId.
  const lines = useAllCartLines()
  const groups = useMemo(() => {
    const byLocation = new Map<string, FamilyCartLine[]>()
    for (const line of lines) {
      const bucket = byLocation.get(line.locationId) ?? []
      bucket.push(line)
      byLocation.set(line.locationId, bucket)
    }
    return [...byLocation.entries()].flatMap(([locationId, locationLines]) =>
      buildCheckoutPlan(locationLines).map((group) => ({ locationId, group })),
    )
  }, [lines])
  return useQueries({
    queries: groups.map(({ locationId, group }) => {
      const vars = {
        serviceLocationId: locationId,
        serviceDate: serviceDateIso(group.date),
        mealtime: group.mealtime,
        mode: 'submit' as const,
        lines: group.lines.map((line) => ({
          menuItemId: line.menuItemId,
          studentUserId: line.studentUserId,
          quantity: line.quantity,
          specialDietAccommodationId: line.specialDietAccommodationId ?? null,
        })),
      }
      const scope = groupKey(group.date, group.mealtime)
      return {
        queryKey: familyOrderPricePreviewQueryKey(vars),
        // The scope rides INSIDE the query data (not by array position):
        // `useQueries` results arrive positionally, and `keepPreviousData`
        // can hand a position retained data from a DIFFERENT key when the
        // group list changes shape — pairing by index would then stamp one
        // sitting's preview onto another. Self-identifying data can't be
        // misattributed.
        queryFn: async ({ signal }: { signal: AbortSignal }) => ({
          scope,
          preview: await fetchFamilyOrderPricePreview(vars, signal),
        }),
        staleTime: 30_000,
        // Every quantity change mints a NEW query key (the lines are part
        // of it) — keep the previous preview rendered while the fresh one
        // loads so prices/badges don't flash blank on each stepper click.
        placeholderData: keepPreviousData,
      }
    }),
    combine: (results) => {
      const entries = scopedPreviewEntries(results)
      const studentsBySitting = new Map<string, FamilyOrderPricePreviewStudentWire>()
      for (const entry of entries) {
        for (const student of entry.students) {
          studentsBySitting.set(sittingStudentKey(entry.scope, student.studentUserId), student)
        }
      }
      return {
        byKey: buildPreviewLookup(entries),
        totals: combinePreviewTotals(results.map((result) => result.data?.preview.totals ?? null)),
        studentsBySitting,
      }
    },
  })
}

/** Per-line lookup helper matching the hook's `byKey` keying. */
export function cartPreviewLineKey(
  date: string,
  mealtime: Mealtime,
  studentUserId: string,
  menuItemId: string,
  specialDietAccommodationId?: string | null,
): string {
  return previewLineKey(groupKey(date, mealtime), studentUserId, menuItemId, specialDietAccommodationId)
}
